/**
 * useSearch — unified pin + place autocomplete.
 *
 * Combines a local in-memory index of currently loaded pins with a
 * debounced Mapbox geocoder call for place names, returning a single
 * ranked suggestion list to the search bar.
 *
 * Consumers: App.tsx feeds the hook into the SearchBar component and
 * uses selectResult() to fly the map to a hit.
 *
 * Matching is diacritic-folded via utils/foldText, so "gravhoj" finds
 * "Gravhøj" and "Aalborg" finds "Ålborg". Folding is for matching only:
 * every value put on a result object is the original, unfolded string.
 */
import { readingLang, walkIn } from "../utils/walkReading.ts";
import { useState, useCallback, useRef, useEffect } from "react";
import type { MutableRefObject } from "react";
import type { MapRef } from "react-map-gl/maplibre";

import type {
  LayerType, FolkloreRecord, RuneStone, HistoricalEvent, Monument,
  ProtectedBuilding, Shipwreck, Museum, RockArt, UnescoSite, IndustrialSite,
  HistoricRoute, CuratedWalk, ThingSite, BarrowSite, MegalithSite, TownscapeSite, PlaqueSite, StolpersteinSite, HeritageSite,
  ByleksikonSite, UnifiedSearchResult, AskFilter, Lang,
} from "../types/index.ts";

import { t } from "../i18n/index.ts";
import { foldText } from "../utils/foldText.ts";

// ============================================================
// FOLD CACHE
// ============================================================
// The layer indexes are built and mutated in App.tsx (pins stream in from
// ~20 lazy fetchers via index.current.set(...)), so there is no single
// build site here where a folded field could be attached once. Instead we
// memoise folds in a module-level Map keyed by the original string.
//
// Why this is the right shape: folding is pure, and the same strings recur
// across every keystroke AND across every record that shares a type or
// municipality, so the cache is warm after the first query and each
// distinct string is folded once for the life of the session rather than
// once per comparison. Bounded so a long session over tens of thousands of
// pins cannot grow it without limit; on overflow we clear and refill.
const FOLD_CACHE_MAX = 50_000;
const foldCache = new Map<string, string>();

function foldCached(s: string): string {
  const hit = foldCache.get(s);
  if (hit !== undefined) return hit;
  const folded = foldText(s);
  if (foldCache.size >= FOLD_CACHE_MAX) foldCache.clear();
  foldCache.set(s, folded);
  return folded;
}

// ============================================================
// INDEX REFS TYPE — all the Map<id, T> refs that searchLocal reads
// ============================================================
export interface SearchIndexRefs {
  folkloreIndex: MutableRefObject<Map<string, FolkloreRecord>>;
  runestoneIndex: MutableRefObject<Map<string, RuneStone>>;
  battlefieldIndex: MutableRefObject<Map<string, HistoricalEvent>>;
  monumentIndex: MutableRefObject<Map<string, Monument>>;
  buildingIndex: MutableRefObject<Map<string, ProtectedBuilding>>;
  shipwreckIndex: MutableRefObject<Map<string, Shipwreck>>;
  museumIndex: MutableRefObject<Map<string, Museum>>;
  rockArtIndex: MutableRefObject<Map<string, RockArt>>;
  unescoIndex: MutableRefObject<Map<string, UnescoSite>>;
  industrialIndex: MutableRefObject<Map<string, IndustrialSite>>;
  barrowIndex: MutableRefObject<Map<string, BarrowSite>>;
  megalithIndex: MutableRefObject<Map<string, MegalithSite>>;
  townscapeIndex: MutableRefObject<Map<string, TownscapeSite>>;
  plaqueIndex: MutableRefObject<Map<string, PlaqueSite>>;
  stolpersteinIndex: MutableRefObject<Map<string, StolpersteinSite>>;
  lighthouseIndex: MutableRefObject<Map<string, HeritageSite>>;
  thingsiteIndex: MutableRefObject<Map<string, ThingSite>>;
  routeIndex: MutableRefObject<Map<string, HistoricRoute>>;
  walkIndex: MutableRefObject<Map<string, CuratedWalk>>;
  byleksikonIndex: MutableRefObject<Map<string, ByleksikonSite>>;
}

export interface UseSearchOptions {
  heritageFeatures: GeoJSON.Feature[];
  indexes: SearchIndexRefs;
  mapRef: MutableRefObject<MapRef | null>;
  lang: Lang;
}

// ============================================================
// matchFields — returns first field value containing the query
// ============================================================
// `query` must already be folded by the caller. The returned value is the
// ORIGINAL field text, since it becomes the visible subtitle.
function matchFields(fields: (string | undefined)[], query: string): string | null {
  for (const f of fields) {
    if (f && foldCached(f).includes(query)) return f;
  }
  return null;
}

// ============================================================
// dedupKey — what counts as "the same row" in the dropdown
// ============================================================
/**
 * Exported for `useSearch.test.ts`. The rule it encodes is the whole reason
 * `place` exists on a search result, and it is easy to get backwards in either
 * direction. Too loose and Plandata's repeated feeds fill the dropdown, too
 * tight and 16 real Dutch windmills disappear. See the comment at the dedup
 * loop for the measurements behind it.
 */
export function dedupKey(
  r: Pick<UnifiedSearchResult, "name" | "layerType" | "place">,
  fold: (s: string) => string,
): string {
  return `${fold(r.name)}|${r.layerType}|${r.place ? fold(r.place) : ""}`;
}

// ============================================================
// getSearchFields — per-layer field sets for multi-field matching
// ============================================================
/**
 * Pin shape held by the layer indexes — heterogeneous across layer types,
 * so we list every field this builder reaches for at least one branch.
 */
interface SearchablePin {
  category?: string;
  placeName?: string;
  genre?: string;
  /** The teller. Carried by the Danish pins so "Bitte Jens" finds his repertoire. */
  informant?: string;
  text?: string | null;
  mythCycle?: string;
  nameIrish?: string;
  characters?: string[];
  inscription?: string;
  monumentType?: string;
  denomination?: string;
  type?: string;
  description?: string;
  municipality?: string;
  wikidataDescription?: string;
  placeType?: string;
  assemblyType?: string;
  routeType?: string;
  commemorated?: string;
  material?: string;
  address?: string;
  city?: string;
  biography?: string;
  extract?: string;
  addr?: string;
  intro?: string;
  /** A walk's stops. The only non-scalar here, read for their names alone. */
  stops?: { name: string }[];
  translations?: Partial<Record<string, { title: string; intro: string }>>;
}

function getSearchFields(pin: SearchablePin, lt: LayerType): (string | undefined)[] {
  switch (lt) {
    case "folklore": return [pin.category, pin.placeName, pin.genre, pin.informant, pin.text?.slice(0, 200), pin.mythCycle, pin.nameIrish, ...(pin.characters ?? [])];
    case "runestone": return [pin.inscription];
    case "monument": return [pin.monumentType, pin.denomination];
    case "building": return [pin.type, pin.description, pin.municipality];
    case "battlefield": return [pin.description, pin.wikidataDescription];
    case "thingsite": return [pin.type, pin.placeType, pin.assemblyType, pin.description];
    case "route": return [pin.routeType, pin.description];
    // A walk is findable by any place it passes, which is how anyone would look
    // for one. The match lands on the walk pin rather than the stop, because the
    // walk is the thing being offered and the stop is a line inside it.
    // The English title and intro too, so a visitor who types in English finds
    // a walk written in Danish.
    case "walk": return [pin.city, pin.intro?.slice(0, 200), pin.translations?.en?.title, pin.translations?.en?.intro?.slice(0, 200), ...(pin.stops ?? []).map(st => st.name)];
    case "plaque": return [pin.inscription, pin.commemorated, pin.material];
    case "stolperstein": return [pin.inscription, pin.address, pin.city, pin.biography];
    case "byleksikon": return [pin.type, pin.extract?.slice(0, 200), pin.addr];
    default: return [pin.type, pin.description]; // shipwreck, museum, rockart, industrial, unesco
  }
}

// ============================================================
// useSearch hook
// ============================================================
/**
 * Is this a question rather than a name? Four words or more. Pure and
 * exported so the line can be tested; a sentence costs a model call.
 */
export function isSearchSentence(q: string): boolean {
  return q.trim().split(/\s+/).length >= 4;
}

export function useSearch({ heritageFeatures, indexes, mapRef, lang }: UseSearchOptions) {
  // --- State ---
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<{ local: UnifiedSearchResult[]; places: UnifiedSearchResult[]; ask?: UnifiedSearchResult | null }>({ local: [], places: [] });
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTrigger, setSearchTrigger] = useState(0);

  // --- Refs ---
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const activeSearchQueryRef = useRef("");
  /**
   * The corpus search's answer for the query it was asked (api/search.ts).
   * Kept apart from the local rows because a pan recomputes those, and the
   * remote rows would vanish with them; withRemote joins the two each time.
   */
  const remoteRef = useRef<{ q: string; hits: UnifiedSearchResult[] }>({ q: "", hits: [] });
  const withRemote = useCallback((local: UnifiedSearchResult[], q: string): UnifiedSearchResult[] => {
    if (remoteRef.current.q !== q.trim()) return local;
    const extra = remoteRef.current.hits.filter((h) => !local.some((l) =>
      l.id === h.id || (foldCached(l.name) === foldCached(h.name) && Math.abs(l.lat - h.lat) < 0.01 && Math.abs(l.lng - h.lng) < 0.02)));
    return [...local, ...extra];
  }, []);
  const searchPanTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Cleanup debounce timers on unmount
  useEffect(() => () => clearTimeout(searchDebounceRef.current), []);
  useEffect(() => () => clearTimeout(searchPanTimerRef.current), []);

  // --- searchLocal: search all indexes + heritage features ---
  const searchLocal = useCallback((q: string): UnifiedSearchResult[] => {
    // Fold the query once; every comparison below runs folded-vs-folded.
    const query = foldText(q);
    if (query.length < 2) return [];
    const results: UnifiedSearchResult[] = [];
    const MAX_PER_LAYER = 10;

    // Heritage: from features array
    let hCount = 0;
    for (const f of heritageFeatures) {
      if (hCount >= MAX_PER_LAYER) break;
      if (f.geometry.type !== "Point") continue;
      const p = f.properties;
      if (!p) continue;
      const name = (p.name as string) || "";
      const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates;
      const nameMatch = foldCached(name).includes(query);
      // altName first among the fallbacks: Canmore's second name is a name,
      // not a description, so a hit on it should read as one in the subtitle.
      const otherMatch = !nameMatch ? matchFields([p.altName as string, p.type as string, p.description as string, p.municipality as string, p.originalFunction as string], query) : null;
      if (!nameMatch && !otherMatch) continue;
      const subtitle = otherMatch ? (otherMatch.length > 60 ? otherMatch.slice(0, 60) + "\u2026" : otherMatch) : undefined;
      results.push({ id: p.id as string, name: name || t(`tab.heritage`, lang), layerType: "heritage", lat, lng, country: p.country as string, subtitle });
      hCount++;
    }

    // All index maps
    interface SearchPin extends SearchablePin {
      id: string;
      name?: string;
      title?: string;
      lat: number;
      lng: number;
      country?: string;
    }
    // The various index maps each hold a layer-specific pin shape; SearchPin is
    // a structural superset that lets us read the common fields. The casts to
    // `Map<string, SearchPin>` widen each map's value type.
    const indexEntries: { index: Map<string, SearchPin>; layerType: LayerType }[] = [
      { index: indexes.folkloreIndex.current, layerType: "folklore" },
      { index: indexes.runestoneIndex.current, layerType: "runestone" },
      { index: indexes.battlefieldIndex.current, layerType: "battlefield" },
      { index: indexes.monumentIndex.current, layerType: "monument" },
      { index: indexes.buildingIndex.current, layerType: "building" },
      { index: indexes.shipwreckIndex.current, layerType: "shipwreck" },
      { index: indexes.museumIndex.current, layerType: "museum" },
      { index: indexes.rockArtIndex.current, layerType: "rockart" },
      { index: indexes.unescoIndex.current, layerType: "unesco" },
      { index: indexes.industrialIndex.current, layerType: "industrial" },
      { index: indexes.barrowIndex.current, layerType: "barrow" },
      { index: indexes.megalithIndex.current, layerType: "megalith" },
      { index: indexes.townscapeIndex.current, layerType: "townscape" },
      { index: indexes.plaqueIndex.current, layerType: "plaque" },
      { index: indexes.stolpersteinIndex.current, layerType: "stolperstein" },
      { index: indexes.lighthouseIndex.current, layerType: "lighthouse" },
      { index: indexes.thingsiteIndex.current, layerType: "thingsite" },
      { index: indexes.routeIndex.current, layerType: "route" },
      { index: indexes.walkIndex.current, layerType: "walk" },
      { index: indexes.byleksikonIndex.current, layerType: "byleksikon" },
    ];

    for (const { index, layerType } of indexEntries) {
      const matches: UnifiedSearchResult[] = [];
      for (const [, pin] of index) {
        if (matches.length >= MAX_PER_LAYER) break;
        // A walk is named in the language it will be read in (walkReading.ts).
        const name: string = layerType === "walk" && pin.translations
          ? walkIn(pin as unknown as CuratedWalk, readingLang(pin as unknown as CuratedWalk, lang)).title
          : pin.name || pin.title || "";
        const nameMatch = foldCached(name).includes(query);
        const otherMatch = !nameMatch ? matchFields(getSearchFields(pin, layerType), query) : null;
        if (!nameMatch && !otherMatch) continue;
        const subtitle = otherMatch ? (otherMatch.length > 60 ? otherMatch.slice(0, 60) + "\u2026" : otherMatch) : undefined;
        // Carried for the dedup key below and shown when nothing else matched.
        // Only the Dutch case is measured; `city` is included because a
        // Stolperstein is named after a person and names repeat, but how often
        // has not been counted.
        const place = pin.municipality ?? pin.city;
        matches.push({ id: pin.id, name: name || t(`tab.${layerType}`, lang), layerType, lat: pin.lat, lng: pin.lng, country: pin.country, subtitle, place });
      }
      results.push(...matches);
    }

    // Deduplicate by name + layer + place. Plandata.dk townscapes emit the same
    // site multiple times via overlapping feeds, and 10+ distinct
    // "Københavns forsyning" cultural environments at different
    // coordinates clutter the dropdown when the user can't tell them
    // apart. Show each name once per layer; the user can pan/refine to
    // disambiguate. Sort puts the highest-relevance instance first, so
    // we keep the first occurrence.
    //
    // `place` is in the key because name alone is not a unique site in every
    // register, and collapsing on it hides real monuments rather than repeat
    // rows. The Dutch register carries 826 names on more than one monument,
    // covering 2 965 of its 8 263 named ones, and a third of those pairs sit
    // over 50 km apart: "De Hoop" is 17 separate windmills, "Welgelegen" 16
    // houses, "Watertoren" 12 water towers. Measured with
    // scripts/measure-rce-names.mjs. The Danish townscapes this dedup was
    // written for are unaffected, because TownscapeSite carries `kommune` and
    // not `municipality`, so their place stays undefined and they collapse.
    const seenKeys = new Set<string>();
    const deduped: UnifiedSearchResult[] = [];
    for (const r of results) {
      // Fold the dedup key too, so "Ålborg" and "Aalborg" variants of the
      // same site from two feeds collapse to one row.
      const key = dedupKey(r, foldCached);
      if (seenKeys.has(key)) continue;
      seenKeys.add(key);
      deduped.push(r);
    }
    results.length = 0;
    results.push(...deduped);

    // Sort by relevance:
    //   1. Name matches (subtitle empty) before field-only matches.
    //      Stops a pin like Domsteinane from out-ranking Bergskogen just
    //      because Bergskogen appears in Domsteinane's description.
    //   2. Within name matches, exact > starts-with > contains.
    //   3. Then alphabetical.
    const PREFERRED_LAYER_RANK: Record<string, number> = {
      heritage: 0, runestone: 0, megalith: 0, monument: 0, unesco: 0,
      shipwreck: 1, rockart: 1, barrow: 1, lighthouse: 1, battlefield: 1,
      museum: 2, building: 2, townscape: 2, industrial: 2, plaque: 2,
      stolperstein: 2, thingsite: 2, route: 2, folklore: 2, byleksikon: 3,
    };
    results.sort((a, b) => {
      const aHasName = !a.subtitle;
      const bHasName = !b.subtitle;
      if (aHasName !== bHasName) return aHasName ? -1 : 1;
      // Rank on folded names against the folded query, otherwise an exact
      // match on "Gravhøj" for the query "gravhoj" would score as rank 3.
      const aName = foldCached(a.name);
      const bName = foldCached(b.name);
      const aRank = aName === query ? 0 : aName.startsWith(query) ? 1 : aName.includes(query) ? 2 : 3;
      const bRank = bName === query ? 0 : bName.startsWith(query) ? 1 : bName.includes(query) ? 2 : 3;
      if (aRank !== bRank) return aRank - bRank;
      // Layer-type tiebreaker: when names tie, prefer layers users typically
      // think of when typing a place name (heritage/runestone/UNESCO over
      // generic museum/byleksikon entries that share the name).
      const aLayer = PREFERRED_LAYER_RANK[a.layerType] ?? 9;
      const bLayer = PREFERRED_LAYER_RANK[b.layerType] ?? 9;
      if (aLayer !== bLayer) return aLayer - bLayer;
      return a.name.localeCompare(b.name);
    });

    return results;
  }, [heritageFeatures, lang, indexes]);

  // --- searchUnified: wraps local search + Nominatim geocoding ---
  const searchUnified = useCallback((q: string) => {
    clearTimeout(searchDebounceRef.current);
    clearTimeout(askDebounceRef.current);
    askAbortRef.current?.abort();
    activeSearchQueryRef.current = q.trim();
    if (!q.trim()) { setSearchResults({ local: [], places: [] }); setSearchLoading(false); return; }

    // Instant local results
    const local = withRemote(searchLocal(q), q);
    setSearchResults(prev => ({ ...prev, local, ask: null }));

    // The rest of the corpus, for what the view has not loaded. Debounced with
    // the geocoder below; a stale answer is dropped by comparing the query.
    if (q.trim().length >= 3 && !isSearchSentence(q)) {
      const asked = q.trim();
      clearTimeout(remoteDebounceRef.current);
      remoteDebounceRef.current = setTimeout(async () => {
        try {
          const res = await fetch(`/api/search?q=${encodeURIComponent(asked)}&limit=12`);
          if (!res.ok) return;
          const { hits } = (await res.json()) as { hits: { id: string; layer: string; name: string; country: string; lat: number; lng: number; type: string; sources: number }[] };
          if (activeSearchQueryRef.current.trim() !== asked) return;
          remoteRef.current = {
            q: asked,
            hits: hits.map((h) => ({
              id: h.id, name: h.name, layerType: h.layer as UnifiedSearchResult["layerType"], lat: h.lat, lng: h.lng, country: h.country,
              subtitle: [h.type, h.sources > 1 ? t("search.sources", lang).replace("{n}", String(h.sources)) : ""].filter(Boolean).join(" · "),
            })),
          };
          setSearchResults(prev => ({ ...prev, local: withRemote(searchLocal(asked), asked) }));
        } catch { /* offline or the function is down: the local rows stand */ }
      }, 350);
    }

    // A sentence, not a name: ask api/ask.ts which of the app's filters it
    // means, and offer that as the first row. Four words is the line because
    // no site or place name in the index is that long and most questions are.
    if (isSearchSentence(q)) {
      askDebounceRef.current = setTimeout(async () => {
        const ctrl = new AbortController();
        askAbortRef.current = ctrl;
        const asked = q.trim();
        try {
          const res = await fetch("/api/ask", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ q: asked, lang }), signal: ctrl.signal });
          if (!res.ok) return;
          const a = (await res.json()) as AskFilter;
          if (ctrl.signal.aborted || activeSearchQueryRef.current !== asked) return;
          if (!a.tab && !a.period && !a.place) return;
          setSearchResults(prev => ({ ...prev, ask: { id: "ask", name: asked, layerType: "ask", lat: 0, lng: 0, ask: a } }));
        } catch { /* aborted or offline: the ordinary results stand */ }
      }, 600);
    }

    // Detect raw coordinates: "55.68, 12.57" or "55.68 12.57"
    const coordMatch = q.trim().match(/^(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)$/);
    if (coordMatch) {
      const lat = parseFloat(coordMatch[1]);
      const lng = parseFloat(coordMatch[2]);
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        setSearchResults({ local, places: [{ id: "coord", name: `${lat.toFixed(4)}, ${lng.toFixed(4)}`, lat, lng, layerType: "place", subtitle: "coordinate" }] });
        setSearchLoading(false);
        return;
      }
    }

    setSearchLoading(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          format: "json", q: q.trim(), viewbox: "-74,49,32,84", bounded: "1", limit: "8",
          "accept-language": lang,
        });
        const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
          headers: { "User-Agent": "Fornland/1.0" },
        });
        if (!res.ok) { setSearchResults(prev => ({ ...prev, places: [] })); setSearchLoading(false); return; }
        const data = await res.json();
        // Loaded here rather than imported: fifteen place types in eleven
        // languages that only a geocoder answer needs, out of the main bundle.
        const { placeTypeLabel } = await import("../utils/placeTypeLabel.ts");
        interface NominatimResult { place_id: string | number; display_name: string; lat: string; lon: string; type?: string; class?: string }
        setSearchResults(prev => ({
          ...prev,
          places: (data as NominatimResult[]).map((r) => ({
            id: `nom-${r.place_id}`, name: r.display_name, lat: parseFloat(r.lat), lng: parseFloat(r.lon),
            layerType: "place" as const, subtitle: placeTypeLabel(r.type, lang),
          })),
        }));
      } catch { setSearchResults(prev => ({ ...prev, places: [] })); }
      setSearchLoading(false);
    }, 400);
  }, [lang, searchLocal]);

  // Re-run local search when map pans (searchTrigger bumped from onMoveEnd)
  useEffect(() => {
    if (searchTrigger === 0) return;
    const q = activeSearchQueryRef.current;
    if (!q) return;
    const local = withRemote(searchLocal(q), q);
    setSearchResults(prev => ({ ...prev, local }));
  }, [searchTrigger, searchLocal, withRemote]);

  // handleSearchSelect — ref-based pattern so App can wire up the implementation later
  const askDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const remoteDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const askAbortRef = useRef<AbortController | null>(null);

  const handleSearchSelectRef = useRef<(result: UnifiedSearchResult) => void>(() => {});
  const handleSearchSelect = useCallback((result: UnifiedSearchResult) => {
    handleSearchSelectRef.current(result);
  }, []);

  // Auto-focus search input when search panel opens.
  //
  // Guarded, because `searchOpen` is set from the input's own onFocus and so
  // flips true again on paths where a dialog has already taken focus. This
  // effect then pulled it straight back out, which is how the detail sheet
  // ended up trapping around an element that never held focus at all: the
  // trap focused the sheet, this ran a tick later, and the reader was left in
  // the search field inside a subtree aria-modal had told them to ignore.
  useEffect(() => {
    if (!searchOpen) return;
    const active = document.activeElement;
    if (active?.closest?.('[role="dialog"][aria-modal="true"]')) return;
    searchInputRef.current?.focus();
  }, [searchOpen]);

  // Dismiss keyboard when map is touched (mobile UX)
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const blurSearch = () => { searchInputRef.current?.blur(); };
    map.on("touchstart", blurSearch);
    return () => { map.off("touchstart", blurSearch); };
  }, [mapRef]);

  return {
    // State
    searchOpen,
    setSearchOpen,
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    searchLoading,
    setSearchLoading,
    searchTrigger,
    setSearchTrigger,

    // Refs
    searchInputRef,
    searchDebounceRef,
    activeSearchQueryRef,
    searchPanTimerRef,

    // Callbacks
    searchLocal,
    searchUnified,
    handleSearchSelect,
    handleSearchSelectRef,
  };
}
